<template>
    <div class="image-magnifier" :data-image-index="imageIndex" :style="{'aspect-ratio': aspectRatio, '--image-mag-ratio': aspectRatio}">
      <div
        v-if="productImage"
        ref="zoomContainer"
        class="image-magnifier__zoom-container"
        :style="containerStyle"
        @mousemove="handleMouseMove"
        @click="toggleZoom"
      >
        <!-- Real <img> instead of background-image: Safari gives style-initiated
             image loads low priority and no progressive paint. -->
        <img
          class="image-magnifier__img"
          :class="{ 'image-magnifier__img--hidden': zoomLayerActive }"
          :src="normalImageUrl"
          :alt="alt"
          :fetchpriority="disable ? 'auto' : 'high'"
          draggable="false"
        />
      </div>
    </div>
  </template>
  
  <script setup>
  import { ref, computed, onUnmounted, watch, onMounted } from 'vue'
  
  // Props
  const props = defineProps({
    productImage: {
      type: String,
      default: ''
    },
    imageIndex: {
      type: Number,
      default: 0
    },
    normalWidth: {
      type: Number,
      default: 2000
    },
    zoomWidth: {
      type: Number,
      default: 4000
    },
    aspectRatio: {
      type: [String, Number],
      default: '1'
    },
    disable: {
      type: Boolean,
      value: false
    },
    // Safari re-downloads CDN images on every dialog open (Vary: Accept), so callers
    // rendering many instances at once should only preload zoom for the active one.
    preloadZoom: {
      type: Boolean,
      default: true
    },
    // Empty by default so decorative uses stay hidden from screen readers
    alt: {
      type: String,
      default: ''
    }
  })
  
  // Reactive refs
  const zoomContainer = ref(null)
  const isZoomed = ref(false)
  const backgroundPosition = ref('center')
  const moveTimeout = ref(null)
  const zoomImagePreloaded = ref(false)
  const zoomPreloadStarted = ref(false)

  // Computed properties
  const normalImageUrl = computed(() => {
    if (!props.productImage) return ''
    return getImageUrl(props.productImage, props.normalWidth)
  })
  
  const zoomImageUrl = computed(() => {
    if (!props.productImage || props.disable) return ''
    return getImageUrl(props.productImage, props.zoomWidth)
  })
  
  const zoomLayerActive = computed(() => {
    return isZoomed.value && !props.disable && zoomImagePreloaded.value
  })

  const containerStyle = computed(() => {
    const baseStyle = {
      transition: 'background-size 0.2s ease-out, background-position 0.05s ease-out',
    }

    // When zoom is disabled (gallery / sticky bar), don't force zoom-in —
    // only the expand icon opens the dialog. Parent can still set cursor
    // (e.g. grid cards use cursor: zoom-in because the card itself is clickable).
    if (!props.disable) {
      baseStyle.cursor = isZoomed.value ? 'zoom-out' : 'zoom-in'
    }

    // The zoomed 4000px layer stays a background image so pan can drive
    // background-position; the base image is the <img> underneath.
    if (zoomLayerActive.value) {
      baseStyle.backgroundImage = `url(${zoomImageUrl.value})`
      baseStyle.backgroundSize = `${props.zoomWidth / props.normalWidth * 100}%` // keep sharp
      baseStyle.backgroundPosition = backgroundPosition.value
      baseStyle.backgroundRepeat = 'no-repeat'
    }

    return baseStyle
  })
  
  // Methods
  const getImageUrl = (imageUrl, width) => {
    // This is a placeholder that assumes you have a way to generate sized URLs
    if (imageUrl.includes('?')) {
      return `${imageUrl}&width=${width}`
    }
    return `${imageUrl}?width=${width}`
  }

  // Preload zoom image immediately when component mounts or image changes
  const preloadZoomImage = () => {
    if (!props.productImage || !zoomImageUrl.value || zoomPreloadStarted.value) return
    zoomPreloadStarted.value = true

    const img = new Image()
    img.onload = () => {
      zoomImagePreloaded.value = true
    }
    img.onerror = () => {
      console.warn('Failed to preload zoom image:', zoomImageUrl.value)
    }
    img.src = zoomImageUrl.value
  }

  const handleMouseMove = (event) => {
    if (!isZoomed.value || !zoomContainer.value || props.disable) return
    
    // Use requestAnimationFrame for smooth 60fps updates
    if (moveTimeout.value) {
      cancelAnimationFrame(moveTimeout.value)
    }
    
    moveTimeout.value = requestAnimationFrame(() => {
      updateZoomPosition(event)
    })
  }
  
  const updateZoomPosition = (event) => {
    if (!zoomContainer.value) return
    
    const rect = zoomContainer.value.getBoundingClientRect()
    const x = event.clientX - rect.left
    const y = event.clientY - rect.top
    const xPercent = (x / rect.width) * 100
    const yPercent = (y / rect.height) * 100
    
    backgroundPosition.value = `${xPercent}% ${yPercent}%`
  }

  const toggleZoom = () => {
    if (props.disable) return
    isZoomed.value = !isZoomed.value
    if (isZoomed.value && !zoomImagePreloaded.value) {
      preloadZoomImage()
    }
    if (!isZoomed.value) {
      backgroundPosition.value = 'center'
    }
  }
  
  // Watch for image changes and preload new zoom image
  watch(() => props.productImage, (newImage, oldImage) => {
    // Reset state
    zoomImagePreloaded.value = false
    zoomPreloadStarted.value = false

    // Only preload if image actually changed
    if (newImage && newImage !== oldImage && props.preloadZoom) {
      // Small delay to batch rapid changes
      setTimeout(() => {
        if (props.productImage === newImage) { // Still the same image
          preloadZoomImage()
        }
      }, 100)
    }
  }, { immediate: true })

  watch(() => props.preloadZoom, (shouldPreload) => {
    if (shouldPreload) {
      preloadZoomImage()
    }
  })

  // Also preload on mount
  onMounted(() => {
    if (props.productImage && props.preloadZoom) {
      preloadZoomImage()
    }
  });
  
  // Cleanup
  onUnmounted(() => {
    if (moveTimeout.value) {
      cancelAnimationFrame(moveTimeout.value)
    }
  });
  </script>
  
  <style scoped>
  .image-magnifier {
    background-color: #f5f5f5;
    overflow: hidden;
    position: relative;
  }
  
  .image-magnifier__zoom-container {
    position: relative;
    min-height: 100%;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  .image-magnifier__img {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  .image-magnifier__img--hidden {
    opacity: 0;
  }
  </style>
